import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, InputLabel, Autocomplete, Grid, ListItemText } from '@mui/material';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import IconButton from '@mui/material/IconButton';
import CheckoutForms from '../../../components/Checkout/CheckoutForms';

const TaskNotesModal = (props) => {
    const [note, setNote] = useState('');
    const [task, setTask] = useState(props.initialTask || '');
    const [taskId, setTaskId] = useState(props.initialTask?.tas_id || '');
    const [showCusForms, setShowCusForms] = useState(false);
    const [forms, setForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [completedForms, setCompletedForms] = useState(props.initialCompletedForms || []);
    const { schedule, customer, onFormsUpdated } = props;
    
    const handleCusFormsClose = () => {
        setShowCusForms(false);
    }

    const handleNoteChange = (e) => {
        setNote(e.target.value)
    }

    const handleFormSubmit = useCallback(() => {
        if (!selectedForm || !schedule || !customer) return;
        const formId = selectedForm.form_id;
        setCompletedForms((prev) => {
            const prevList = Array.isArray(prev) ? prev : [];
            if (prevList.includes(formId)) {
                return prevList;
            }
            return [...prevList, formId];
        });
        if (props.onFormsUpdated) props.onFormsUpdated();
    }, [selectedForm, schedule, customer, props.onFormsUpdated]);

    useEffect(() => {
        if (props.open) {
            if (props.initialTask) {
                setTask(props.initialTask);
                setTaskId(props.initialTask.tas_id);
                setNote(props.initialTask.tas_notes || '');
            } else {
                getTask();
            }
        } else {
            setNote('');
        }
    }, [props.open])

    useEffect(() => {
        if (props.initialCompletedForms) {
            setCompletedForms(props.initialCompletedForms);
        } else {
            async function fetchCompletedForms() {
                if (!taskId || !props.customer?.cus_id || !forms.length) return;
                try {
                    const completed = [];
                    for (const form of forms) {
                        const res = await axios.get(`${APIURL}/customer_form/task/${taskId}`, { params: { formType: form.form_id } });
                        if (Array.isArray(res.data) ? res.data.length > 0 : !!res.data) {
                            completed.push(form.form_id);
                        }
                    }
                    setCompletedForms(completed);
                } catch (e) {
                    setCompletedForms([]);
                }
            }
            fetchCompletedForms();
        }
    }, [taskId, props.customer, forms, props.initialCompletedForms]);

    useEffect(() => {
        if (props.open) {
            if (false) { //making it false to always fetch forms when modal opens to fix QA issue
                setForms(props.initialForms);
            } else if (task && task.tas_type) {
                getForms();
            }
        }
    }, [task, props.initialForms])

    const getTask = async () => {
        try {
            let session = props.sessions.filter((session) => session.schedule_id === props.schedule.schedule_id && session.customer_id === props.customer.cus_id)
            if (session.length > 0 && session[0].task_id != null) {
                const res = await axios.get(APIURL + '/task', { params: { taskId: session[0].task_id } })
                setNote(res.data[0]?.tas_notes)
                setTask(res.data[0])
                setTaskId(res.data[0]?.tas_id)
            } else {
                const res = await axios.get(`${APIURL}/task/session`, {
                    params: {
                        tas_type: props.schedule.task_type,
                        tas_title: props.schedule.schedule_name,
                        tas_start_date: moment(props.schedule.start_time).format('YYYY-MM-DD HH:mm:ss'),
                        tas_end_date: moment(props.schedule.end_time).format('YYYY-MM-DD HH:mm:ss'),
                        tas_com_id: props.comp_id,
                        cus_id: props.customer.cus_id
                    }
                })
                setNote(res.data.data?.tas_notes)
                setTask(res.data.data)
                setTaskId(res.data.data?.tas_id)
            }   
        } catch (error) {
            props.onError("Error fetching task notes")
            console.log(error)
        }
    }

    const getForms = async () => {
        try {
            console.log(task);
            
            let taskTypes = await axios.get(`${APIURL}/dropdowns/dropdownType/fullTaskType`, { params: { comp_id: props.comp_id } })
            let typeMatch = taskTypes.data.find((type) => type.EntityValue === task.tas_type);
            if (!typeMatch) return;
            let taskTypeId = typeMatch.ddID;
            let forms = await axios.get(`${APIURL}/task_form`, { params: { taskType_id: taskTypeId, company_id: props.comp_id } })
            setForms(forms.data)
        } catch (error) {
            props.onError("Error fetching task forms")
            console.log(error)
        }
    }

    const handleSave = async () => {
        try {
            console.log(taskId, note);
            
            const res = await axios.put(`${APIURL}/task/notes`, {
                tas_id: taskId,
                tas_notes: note
            })
            props.onSuccess("Task notes updated successfully")
            props.onClose()
        } catch (error) {
            props.onError("Error updating task notes")
            console.log(error)
        }
    }

    return (
        <Box>
            <Modal enforceFocus={false}
                open={props.open}
                onClose={props.onClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                            Task Notes
                        </Typography>
                        <IconButton onClick={() => setShowCusForms(true)}>
                            <FormatListBulletedIcon />
                        </IconButton>
                    </Box>
                    <TextField
                        id="outlined-multiline-static"
                        label="Notes"
                        multiline
                        rows={6}
                        value={note}
                        onChange={handleNoteChange}
                        fullWidth
                    />
                    <Button fullWidth onClick={() => handleSave()} variant='contained' sx={{ mt: 2 }}>Save</Button>
                </Box>
            </Modal>
            <Modal enforceFocus={false}
                open={showCusForms}
                onClose={handleCusFormsClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 800,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Forms
                    </Typography>
                    <CheckoutForms 
                        forms={forms}
                        completedForms={completedForms}
                        onSubmit={handleFormSubmit}
                        onEdit={() => {}}
                        customer_id={props.customer?.cus_id}
                        customer_name={props.customer?.cus_name}
                        task_id={taskId}
                        taskDetails={task}
                        selectedForm={selectedForm}
                        setSelectedForm={setSelectedForm}
                        user_id={props.user.emp_id}
                    />
                </Box>
            </Modal>
        </Box>
    )
}

export default TaskNotesModal;

